import { PublicAxiosInstance } from '@/api/axios'
import { logoImages, utilsImages } from '@/assets/images'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { FullPageSkeleton } from '@/components/ui/skeleton'
import { endPoint } from '@/constant/end-point'
import useApiMutation from '@/hooks/use-api-mutation'
import { useTheme } from '@/hooks/use-theme'
import { cn } from '@/lib/utils'
import {
  vendorRegistrationSchema,
  type VendorRegistrationSchemaType,
} from '@/schema/organisation/service-provider.schema'
import { useAuth } from '@clerk/clerk-react'
import { zodResolver } from '@hookform/resolvers/zod'
import axios from 'axios'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'

const VENDOR_INVITATION_TOKEN_KEY = 'vendorInvitationToken'

interface IClaimExistingPayload {
  token: string
}

const VendorRegistrationPage = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const navigate = useNavigate()
  const { theme } = useTheme()
  const { isLoaded: authLoaded, isSignedIn } = useAuth()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [claiming, setClaiming] = useState(false)
  const claimAttempted = useRef(false)

  const { createMutation: claimExistingMutation } = useApiMutation<IClaimExistingPayload>({
    endpoint: endPoint.portal.claimExisting,
    customQueryKey: [endPoint.portal.claimExisting],
  })

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<VendorRegistrationSchemaType>({
    resolver: zodResolver(vendorRegistrationSchema),
    defaultValues: { email: '', firstName: '', lastName: '', password: '', confirmPassword: '' },
  })

  const validate = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await PublicAxiosInstance.get(endPoint.portal.invitationValidate, { params: { token } })
      const email = response.data?.data?.email as string | undefined
      if (email) setValue('email', email)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 410) {
        setError('This invitation link has expired or has already been used.')
      } else {
        setError(
          axios.isAxiosError(err)
            ? (err.response?.data?.message ?? 'Invalid invitation link')
            : 'Invalid invitation link'
        )
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (token) void validate()
    else setLoading(false)
  }, [token])

  useEffect(() => {
    if (!authLoaded || !isSignedIn || !token || loading || error || claimAttempted.current) return
    claimAttempted.current = true

    const claim = async () => {
      try {
        setClaiming(true)
        await claimExistingMutation.mutateAsync({ token })
        localStorage.removeItem(VENDOR_INVITATION_TOKEN_KEY)
        toast.success('Invitation accepted')
        navigate('/vendor/assessments', { replace: true })
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Could not accept the invitation. It may have expired or already been used.'
        )
      } finally {
        setClaiming(false)
      }
    }
    void claim()
  }, [authLoaded, isSignedIn, token, loading, error, navigate])

  const onSubmit = async (data: VendorRegistrationSchemaType) => {
    try {
      // Public unauthenticated claim - PublicAxiosInstance (baseURL includes /public)
      const response = await PublicAxiosInstance.post(endPoint.portal.invitationClaim, {
        token,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
      })

      const existingUser = response.data?.data?.existingUser as boolean | undefined
      if (existingUser) {
        toast.info('An account already exists for this email. Please sign in to accept the invitation.')
        localStorage.setItem(VENDOR_INVITATION_TOKEN_KEY, token)
        navigate(`/sign-in?invitationToken=${encodeURIComponent(token)}`)
        return
      }

      toast.success('Account created. Please sign in to continue.')
      navigate('/sign-in')
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data?.message ?? 'Failed to complete registration')
        : 'Failed to complete registration'
      toast.error(message)
    }
  }

  if (!token) return <Navigate to={'/'} />

  if (loading || claiming || (authLoaded && isSignedIn && !error)) return <FullPageSkeleton />

  if (error)
    return (
      <div className='flex min-h-screen items-center justify-center p-4'>
        <div className='text-center'>
          <div className='mb-4 text-6xl text-red-600'>⚠️</div>
          <h2 className='mb-2 text-2xl font-bold'>Invitation unavailable</h2>
          <p className='mb-4 text-gray-600'>{error}</p>
          <Button onClick={() => navigate('/sign-in')}>Go to sign in</Button>
        </div>
      </div>
    )

  return (
    <div className='flex min-h-screen w-full items-center justify-center bg-background p-4'>
      <div className={cn('flex w-full flex-col gap-6 xl:w-[60%] lg:w-[80%] md:w-full')}>
        <Card className='overflow-hidden p-0 shadow-md'>
          <CardContent className='grid p-0 md:grid-cols-2'>
            <form onSubmit={handleSubmit(onSubmit)} className='p-6 md:p-8'>
              <div className='flex flex-col gap-4'>
                <div className='flex flex-col items-center text-center'>
                  <img
                    loading='lazy'
                    decoding='async'
                    src={theme === 'dark' ? logoImages.logoLight : logoImages.logoBlack}
                    alt='Image'
                    className='h-12 w-auto'
                  />
                  <h1 className='text-2xl font-medium pt-3'>Complete your registration</h1>
                  <p className='text-muted-foreground text-sm'>
                    Set up your account to access the assessment you have been invited to.
                  </p>
                </div>

                <Input
                  label='Email'
                  type='email'
                  readOnly
                  className='cursor-not-allowed bg-muted/50 opacity-70'
                  error={errors?.email?.message}
                  {...register('email')}
                />

                <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                  <Input
                    label='First Name'
                    required
                    placeholder='Enter your first name'
                    error={errors?.firstName?.message}
                    {...register('firstName')}
                  />
                  <Input
                    label='Last Name'
                    required
                    placeholder='Enter your last name'
                    error={errors?.lastName?.message}
                    {...register('lastName')}
                  />
                </div>

                <Input
                  label='Password'
                  type='password'
                  required
                  placeholder='Create a password'
                  error={errors?.password?.message}
                  {...register('password')}
                />
                <Input
                  label='Confirm Password'
                  type='password'
                  required
                  placeholder='Re-enter your password'
                  error={errors?.confirmPassword?.message}
                  {...register('confirmPassword')}
                />

                <Button type='submit' className='w-full' disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className='h-4 w-4 animate-spin' />
                      <span>Creating account...</span>
                    </>
                  ) : (
                    'Create account'
                  )}
                </Button>

                <div className='text-center text-sm'>
                  Already have an account?&nbsp;
                  <Link
                    to={`/sign-in?invitationToken=${encodeURIComponent(token)}`}
                    className='underline underline-offset-4'
                    onClick={() => localStorage.setItem(VENDOR_INVITATION_TOKEN_KEY, token)}
                  >
                    Sign in
                  </Link>
                </div>
              </div>
            </form>
            <div className='relative hidden md:block'>
              <img
                loading='lazy'
                decoding='async'
                src={utilsImages.loginPageSide}
                alt='Image'
                className='absolute inset-0 h-full w-full object-cover dark:grayscale'
              />
            </div>
          </CardContent>
        </Card>
        <div className='text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4'>
          By clicking continue, you agree to our <Link to='/terms-of-service'>Terms of Service</Link> and&nbsp;
          <Link to='/privacy-policy'>Privacy Policy</Link>.
        </div>
      </div>
    </div>
  )
}

export default VendorRegistrationPage
